import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import { useAuth } from '../../../contexts/AuthContext';
import { useProfile } from '../../../hooks';
import {
  getDefaultAuthenticatedPath,
  isBillingOnlyPath,
  isDemoIncomplete,
  needsSubscriptionSelection,
} from '../../../lib/utils/subscriptionAccess';

const LoginForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, user } = useAuth();
  const { profile, loading: profileLoading } = useProfile();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const getRedirectPath = () => {
    if (isDemoIncomplete(profile)) return '/demo';

    const from = location.state?.from?.pathname;
    if (
      from &&
      (!needsSubscriptionSelection(profile) || isBillingOnlyPath(from))
    ) {
      return from;
    }
    return getDefaultAuthenticatedPath(profile);
  };

  // Redirect if already logged in
  useEffect(() => {
    if (user && !profileLoading) {
      navigate(getRedirectPath(), { replace: true });
    }
  }, [user, profile, profileLoading, navigate, location.state?.from?.pathname]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email) {
      newErrors.email = t('auth.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = t('auth.emailInvalid');
    }

    if (!formData?.password) {
      newErrors.password = t('auth.passwordRequired');
    } else if (formData?.password?.length < 6) {
      newErrors.password = t('auth.passwordMinLength', { count: 6 });
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setErrors({});

    try {
      const { data, error } = await signIn(formData?.email, formData?.password, formData?.rememberMe);
      
      if (error) {
        if (error.message?.includes('Invalid login credentials')) {
          setErrors({
            email: t('auth.invalidCredentials'),
            password: t('auth.invalidCredentials')
          });
        } else if (error.message?.includes('Email not confirmed')) {
          setErrors({
            email: t('auth.emailNotConfirmed')
          });
        } else {
          setErrors({
            email: error.message || t('auth.loginFailed')
          });
        }
      } else {
        // Redirect happens in useEffect after profile loads
      }
    } catch (error) {
      setErrors({
        email: t('common.unexpectedError')
      });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    navigate('/forgot-password');
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <Input
        label={t('auth.email')}
        type="email"
        name="email"
        placeholder={t('auth.emailPlaceholder')}
        value={formData?.email}
        onChange={handleChange}
        error={errors?.email}
        required
        disabled={loading}
        dense
      />
      <Input
        label={t('auth.password')}
        type="password"
        name="password"
        placeholder={t('auth.passwordPlaceholder')}
        value={formData?.password}
        onChange={handleChange}
        error={errors?.password}
        required
        disabled={loading}
        dense
      />
      <div className="flex items-center justify-between">
        <Checkbox
          label={t('auth.rememberMe')}
          name="rememberMe"
          checked={formData?.rememberMe}
          onChange={handleChange}
          disabled={loading}
        />
        <button
          type="button"
          onClick={handleForgotPassword}
          className="text-sm text-primary hover:text-accent transition-colors underline underline-offset-2"
          disabled={loading}
        >
          {t('auth.forgotPassword')}
        </button>
      </div>
      <Button
        type="submit"
        variant="default"
        fullWidth
        loading={loading}
        className="mt-4"
      >
        {t('auth.loginCta')}
      </Button>
    </form>
  );
};

export default LoginForm;
